import { useEffect, useState } from 'react'
import { Card } from '@/components/Card'
import { StatusBadge } from '@/components/StatusBadge'
import { fetchApi } from '@/lib/api'
import { RefreshCw, ExternalLink, ChevronDown, ChevronRight, Clock, AlertCircle } from 'lucide-react'
import { formatDateTime, formatTime } from '@/lib/date'

type Deployment = {
  uid: string
  name: string
  url: string
  state: string
  target: string | null
  created: number
  creator: string | null
  commit_message: string
  commit_sha: string
  branch: string
}

type Project = {
  id: string
  name: string
  framework: string | null
  updated_at: number | null
  latest_deployment: Deployment | null
}

type BuildEvent = {
  timestamp: number
  type: string
  text: string
}

type RequestLogEntry = {
  timestamp: number
  message: string
}

type RequestLog = {
  request_id: string
  timestamp: number
  method: string
  path: string
  status_code: number | null
  duration_ms: number | null
  domain: string
  cache: string
  environment: string
  deployment_id: string
  client_region: string
  user_agent: string
  has_function_crashed: boolean
  logs: RequestLogEntry[]
}

type Tab = 'deployments' | 'logs'

function stateToStatus(state: string): string {
  switch (state?.toUpperCase()) {
    case 'READY': return 'ok'
    case 'BUILDING': case 'INITIALIZING': case 'QUEUED': return 'pending'
    case 'ERROR': return 'error'
    case 'CANCELED': return 'unknown'
    default: return 'unknown'
  }
}

function statusCodeColor(code: number | null): string {
  if (!code) return 'text-[var(--color-text-muted)]'
  if (code >= 500) return 'text-red-400'
  if (code >= 400) return 'text-orange-400'
  if (code >= 300) return 'text-yellow-400'
  return 'text-green-400'
}

export function Vercel() {
  const [projects, setProjects] = useState<Project[]>([])
  const [selectedProject, setSelectedProject] = useState<string>('')
  const [deployments, setDeployments] = useState<Deployment[]>([])
  const [loading, setLoading] = useState(true)
  const [deploymentsLoading, setDeploymentsLoading] = useState(false)
  const [expandedDeploy, setExpandedDeploy] = useState<string | null>(null)
  const [buildLogs, setBuildLogs] = useState<Record<string, BuildEvent[]>>({})
  const [buildLogsLoading, setBuildLogsLoading] = useState<string | null>(null)

  // Logs tab state
  const [activeTab, setActiveTab] = useState<Tab>('deployments')
  const [requestLogs, setRequestLogs] = useState<RequestLog[]>([])
  const [requestLogsLoading, setRequestLogsLoading] = useState(false)
  const [logsMinutes, setLogsMinutes] = useState(60)
  const [logsStatusFilter, setLogsStatusFilter] = useState('')
  const [logsPathFilter, setLogsPathFilter] = useState('')
  const [expandedLog, setExpandedLog] = useState<string | null>(null)

  const loadProjects = async () => {
    setLoading(true)
    try {
      const data = await fetchApi<{ projects: Project[] }>('/vercel/projects')
      setProjects(data.projects)
      if (data.projects.length > 0 && !selectedProject) {
        setSelectedProject(data.projects[0].id)
      }
    } catch {
      setProjects([])
    } finally {
      setLoading(false)
    }
  }

  const loadDeployments = async (projectId: string) => {
    setDeploymentsLoading(true)
    try {
      const data = await fetchApi<{ deployments: Deployment[] }>(
        `/vercel/deployments?project_id=${projectId}&limit=20`
      )
      setDeployments(data.deployments)
    } catch {
      setDeployments([])
    } finally {
      setDeploymentsLoading(false)
    }
  }

  const loadBuildLogs = async (deploymentId: string) => {
    if (buildLogs[deploymentId]) {
      setExpandedDeploy(expandedDeploy === deploymentId ? null : deploymentId)
      return
    }
    setExpandedDeploy(deploymentId)
    setBuildLogsLoading(deploymentId)
    try {
      const data = await fetchApi<{ events: BuildEvent[] }>(
        `/vercel/deployments/${deploymentId}/events`
      )
      setBuildLogs((prev) => ({ ...prev, [deploymentId]: data.events }))
    } catch {
      setBuildLogs((prev) => ({ ...prev, [deploymentId]: [] }))
    } finally {
      setBuildLogsLoading(null)
    }
  }

  const loadRequestLogs = async (projectId: string) => {
    setRequestLogsLoading(true)
    try {
      const params = new URLSearchParams({
        project_id: projectId,
        minutes: String(logsMinutes),
      })
      if (logsStatusFilter) params.set('status_code', logsStatusFilter)
      if (logsPathFilter) params.set('path', logsPathFilter)
      const data = await fetchApi<{ logs: RequestLog[] }>(
        `/vercel/logs?${params}`
      )
      setRequestLogs(data.logs)
    } catch {
      setRequestLogs([])
    } finally {
      setRequestLogsLoading(false)
    }
  }

  useEffect(() => { loadProjects() }, [])
  useEffect(() => {
    if (selectedProject) {
      if (activeTab === 'deployments') {
        loadDeployments(selectedProject)
      } else {
        loadRequestLogs(selectedProject)
      }
    }
  }, [selectedProject, activeTab])

  const selectedProjectName = projects.find(p => p.id === selectedProject)?.name

  const handleRefresh = () => {
    if (!selectedProject) return
    if (activeTab === 'deployments') {
      loadDeployments(selectedProject)
    } else {
      loadRequestLogs(selectedProject)
    }
  }

  const isLoading = loading || deploymentsLoading || requestLogsLoading

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Vercel</h2>
        <div className="flex items-center gap-2">
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="px-3 py-1.5 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text)]"
          >
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex items-center gap-1 px-3 py-1.5 text-sm rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] transition-colors"
          >
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Project summary */}
      {selectedProjectName && (
        <div className="flex items-center gap-4 text-sm text-[var(--color-text-muted)]">
          <span className="font-mono">{selectedProjectName}</span>
          {projects.find(p => p.id === selectedProject)?.framework && (
            <span className="px-2 py-0.5 rounded bg-[var(--color-bg-hover)] text-xs">
              {projects.find(p => p.id === selectedProject)?.framework}
            </span>
          )}
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex gap-1 border-b border-[var(--color-border)]">
        <button
          onClick={() => setActiveTab('deployments')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'deployments'
              ? 'border-blue-500 text-blue-400'
              : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
          }`}
        >
          Deployments
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'logs'
              ? 'border-blue-500 text-blue-400'
              : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
          }`}
        >
          Request Logs
        </button>
      </div>

      {/* Deployments tab */}
      {activeTab === 'deployments' && (
        <Card title={`Deployments${selectedProjectName ? ` - ${selectedProjectName}` : ''}`}>
          {(loading || deploymentsLoading) && deployments.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">Loading...</p>
          ) : deployments.length > 0 ? (
            <div className="space-y-1">
              {deployments.map((d) => (
                <div key={d.uid}>
                  <div
                    className="flex items-center justify-between p-3 rounded bg-[var(--color-bg)] cursor-pointer hover:bg-[var(--color-bg-hover)] transition-colors"
                    onClick={() => loadBuildLogs(d.uid)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {expandedDeploy === d.uid ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <StatusBadge status={stateToStatus(d.state)} label={d.state} />
                        {d.target && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            d.target === 'production'
                              ? 'bg-green-500/10 text-green-400'
                              : 'bg-blue-500/10 text-blue-400'
                          }`}>
                            {d.target}
                          </span>
                        )}
                        <span className="text-sm truncate">{d.commit_message || d.name}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 ml-5 text-xs text-[var(--color-text-muted)]">
                        {d.branch && <span className="font-mono">{d.branch}</span>}
                        {d.commit_sha && <span className="font-mono">{d.commit_sha}</span>}
                        {d.creator && <span>{d.creator}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-4 shrink-0">
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {formatDateTime(d.created)}
                      </span>
                      {d.url && (
                        <a
                          href={`https://${d.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Build logs */}
                  {expandedDeploy === d.uid && (
                    <div className="mx-3 mb-2 p-3 rounded bg-[var(--color-bg)] border border-[var(--color-border)]">
                      {buildLogsLoading === d.uid ? (
                        <p className="text-xs text-[var(--color-text-muted)]">Loading build logs...</p>
                      ) : (buildLogs[d.uid]?.length ?? 0) > 0 ? (
                        <div className="max-h-64 overflow-auto">
                          <pre className="text-xs font-mono text-[var(--color-text-muted)] whitespace-pre-wrap">
                            {buildLogs[d.uid].map((e, i) => (
                              <div key={i} className={e.type === 'stderr' || e.type === 'error' ? 'text-red-400' : ''}>
                                {e.text}
                              </div>
                            ))}
                          </pre>
                        </div>
                      ) : (
                        <p className="text-xs text-[var(--color-text-muted)]">No build logs available</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--color-text-muted)]">No deployments found</p>
          )}
        </Card>
      )}

      {/* Request Logs tab */}
      {activeTab === 'logs' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-[var(--color-text-muted)]" />
              <select
                value={logsMinutes}
                onChange={(e) => setLogsMinutes(Number(e.target.value))}
                className="px-2 py-1 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text)]"
              >
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={60}>1 hour</option>
                <option value={180}>3 hours</option>
                <option value={360}>6 hours</option>
                <option value={720}>12 hours</option>
                <option value={1440}>24 hours</option>
              </select>
            </div>
            <input
              type="text"
              value={logsStatusFilter}
              onChange={(e) => setLogsStatusFilter(e.target.value)}
              placeholder="Status (e.g. 500, 4xx)"
              className="px-2 py-1 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text)] w-36"
            />
            <input
              type="text"
              value={logsPathFilter}
              onChange={(e) => setLogsPathFilter(e.target.value)}
              placeholder="Path filter (e.g. /api)"
              className="px-2 py-1 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text)] w-48"
            />
            <button
              onClick={() => selectedProject && loadRequestLogs(selectedProject)}
              disabled={requestLogsLoading || !selectedProject}
              className="flex items-center gap-1 px-3 py-1 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
            >
              {requestLogsLoading ? <RefreshCw size={14} className="animate-spin" /> : 'Search'}
            </button>
          </div>

          {/* Results */}
          <Card title={`Request Logs${selectedProjectName ? ` - ${selectedProjectName}` : ''}`}>
            {requestLogsLoading ? (
              <p className="text-sm text-[var(--color-text-muted)]">Loading request logs...</p>
            ) : requestLogs.length > 0 ? (
              <div className="space-y-1">
                {requestLogs.map((log) => (
                  <div key={log.request_id}>
                    <div
                      className="flex items-center justify-between p-3 rounded bg-[var(--color-bg)] cursor-pointer hover:bg-[var(--color-bg-hover)] transition-colors"
                      onClick={() => setExpandedLog(expandedLog === log.request_id ? null : log.request_id)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {expandedLog === log.request_id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          <span className={`text-sm font-mono font-bold ${statusCodeColor(log.status_code)}`}>
                            {log.status_code || '---'}
                          </span>
                          <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-[var(--color-bg-hover)]">
                            {log.method}
                          </span>
                          <span className="text-sm font-mono truncate">{log.path}</span>
                          {log.has_function_crashed && (
                            <span className="flex items-center gap-1 text-xs text-red-400">
                              <AlertCircle size={12} /> CRASHED
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 ml-5 text-xs text-[var(--color-text-muted)]">
                          <span>{log.domain}</span>
                          {log.cache && <span className="font-mono">{log.cache}</span>}
                          {log.client_region && <span>{log.client_region}</span>}
                          {log.logs.length > 0 && (
                            <span className="text-blue-400">{log.logs.length} log lines</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 ml-4 shrink-0 text-xs text-[var(--color-text-muted)]">
                        {log.duration_ms != null && (
                          <span className={`font-mono ${log.duration_ms > 5000 ? 'text-orange-400' : ''}`}>
                            {log.duration_ms}ms
                          </span>
                        )}
                        <span>
                          {formatDateTime(log.timestamp)}
                        </span>
                      </div>
                    </div>

                    {/* Expanded log details */}
                    {expandedLog === log.request_id && (
                      <div className="mx-3 mb-2 p-3 rounded bg-[var(--color-bg)] border border-[var(--color-border)] space-y-3">
                        {/* Metadata */}
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                          <div>
                            <span className="text-[var(--color-text-muted)]">Request ID: </span>
                            <span className="font-mono">{log.request_id}</span>
                          </div>
                          <div>
                            <span className="text-[var(--color-text-muted)]">Deployment: </span>
                            <span className="font-mono">{log.deployment_id}</span>
                          </div>
                          <div>
                            <span className="text-[var(--color-text-muted)]">Environment: </span>
                            <span>{log.environment || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-[var(--color-text-muted)]">Cache: </span>
                            <span className="font-mono">{log.cache || 'N/A'}</span>
                          </div>
                          {log.user_agent && (
                            <div className="col-span-2">
                              <span className="text-[var(--color-text-muted)]">User-Agent: </span>
                              <span className="font-mono text-[10px]">{log.user_agent}</span>
                            </div>
                          )}
                        </div>

                        {/* Function logs */}
                        {log.logs.length > 0 && (
                          <div>
                            <div className="text-xs text-[var(--color-text-muted)] mb-1 font-medium">Function Logs</div>
                            <div className="max-h-48 overflow-auto">
                              <pre className="text-xs font-mono text-[var(--color-text-muted)] whitespace-pre-wrap">
                                {log.logs.map((entry, i) => (
                                  <div key={i}>
                                    <span className="text-[var(--color-text-muted)] opacity-50">
                                      {formatTime(entry.timestamp)}
                                    </span>
                                    {' '}{entry.message}
                                  </div>
                                ))}
                              </pre>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[var(--color-text-muted)]">
                No request logs found. Click "Search" to load logs.
              </p>
            )}
          </Card>
        </div>
      )}
    </div>
  )
}
